import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { Observable, Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Reactive Controller for managing RxJS Observable lifecycle with reconnect-safety.
 *
 * Provides a `disconnected$` Subject that can be used with `takeUntil()` to automatically
 * unsubscribe from Observables when the host element disconnects from the DOM.
 *
 * **Reconnect-Safe Pattern (M-NEW-1):**
 * The `disconnected$` Subject is recreated on each `hostConnected()` call after
 * `.complete()` has been called. This prevents "Cannot subscribe to a closed Subject"
 * errors when a component disconnects and later reconnects (e.g., route navigation).
 *
 * @remarks
 * This controller replaces the manual `_destroy$ = new Subject<void>()` pattern
 * used in legacy components. The reconnect-safe implementation ensures that
 * components can be safely moved in and out of the DOM multiple times without
 * subscription failures.
 *
 * **Why disconnected$ is NOT readonly:**
 * Subjects cannot be reused after `.complete()` is called. On reconnect, we must
 * replace the closed Subject with a fresh instance. Making it readonly would
 * prevent this necessary mutation.
 *
 * @example
 * ```ts
 * class MyComponent extends LitElement {
 *     private readonly rxjs = new RxjsLifecycleController(this);
 *
 *     connectedCallback() {
 *         super.connectedCallback();
 *         someObservable$.pipe(
 *             takeUntil(this.rxjs.disconnected$)
 *         ).subscribe(() => {
 *             // This will auto-unsubscribe on disconnect
 *         });
 *     }
 * }
 * ```
 */
export class RxjsLifecycleController implements ReactiveController {
    /**
     * Subject that emits and completes when the host disconnects.
     * Use with `takeUntil(this.disconnected$)` to auto-unsubscribe Observables.
     *
     * **Note:** NOT readonly - recreated on reconnect after `.complete()`.
     */
    public disconnected$ = new Subject<void>();

    constructor(private readonly host: ReactiveControllerHost) {
        host.addController(this);
    }

    /**
     * Called when host element connects to the DOM.
     * Recreates disconnected$ Subject if it was closed during previous disconnect.
     */
    hostConnected(): void {
        // Re-create Subject if previous lifecycle closed it (M-NEW-1)
        if (this.disconnected$.closed) {
            this.disconnected$ = new Subject<void>();
        }
    }

    /**
     * Called when host element disconnects from the DOM.
     * Completes disconnected$ Subject to trigger all takeUntil() unsubscriptions.
     */
    hostDisconnected(): void {
        this.disconnected$.next();
        this.disconnected$.complete();
    }

    /**
     * Convenience for the `fromEvent(target, eventName).pipe(takeUntil(this
     * .disconnected$))` idiom (ADR-007, Stefan 2026-09-06: added after an
     * inheritance-based version of this was mistakenly added directly to the
     * legacy `AbstractLitElement` base class instead of here — Composition
     * over Inheritance, per ADR-007 §6). Returns the Observable (not a
     * subscription) so callers keep composing it with other operators the
     * way `abstract-o2d-timeline.view.ts`'s own event streams do.
     *
     * @example
     * ```ts
     * class MyComponent extends AbstractO2dElement {
     *     private readonly rxjs = new RxjsLifecycleController(this);
     *
     *     connectedCallback() {
     *         super.connectedCallback();
     *         this.rxjs.listenUntilDisconnected(window, 'resize')
     *             .subscribe(() => { ... });
     *     }
     * }
     * ```
     */
    public listenUntilDisconnected<T extends Event = Event>(target: EventTarget, eventName: string): Observable<T> {
        return fromEvent<T>(target, eventName).pipe(takeUntil(this.disconnected$));
    }
}
